import React from "react";
import axios from "axios";
import { TrendingUp, DollarSign, ShoppingBag, Target, Users, Truck, Activity, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dkk = (n) => `${Math.round(Number(n) || 0).toLocaleString("da-DK")} kr`;
const num = (n) => Number(n || 0).toLocaleString("da-DK");

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-brand p-5 relative overflow-hidden">
    <div className="flex items-start justify-between mb-3">
      <Icon size={18} className="text-brand-text2" />
      {accent && <span className={`text-[10px] uppercase tracking-widest font-semibold ${accent}`}>{accent === "text-emerald-600" ? "↑" : "↓"}</span>}
    </div>
    <div className="font-display font-bold text-2xl text-brand-dark">{value}</div>
    <div className="text-xs uppercase tracking-widest text-brand-text2 mt-1">{label}</div>
    {sub && <div className="text-xs text-brand-text2 mt-2">{sub}</div>}
  </div>
);

// Simple SVG bar chart — no external libs needed
const BarChart = ({ data, dataKey = "revenue", labelKey = "date", height = 220 }) => {
  if (!data?.length) return <div className="text-brand-text2 text-sm">Ingen data endnu</div>;
  const max = Math.max(...data.map((d) => Number(d[dataKey]) || 0), 1);
  const barW = 100 / data.length;
  return (
    <div className="w-full" style={{ height }}>
      <svg viewBox={`0 0 100 100`} preserveAspectRatio="none" className="w-full h-full">
        {data.map((d, i) => {
          const v = Number(d[dataKey]) || 0;
          const h = (v / max) * 95;
          return (
            <g key={i}>
              <rect
                x={i * barW + 0.5}
                y={100 - h}
                width={barW - 1}
                height={h}
                fill="#DC2626"
                opacity={v > 0 ? 0.85 : 0.15}
              >
                <title>{`${d[labelKey]}: ${dkk(v)}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-brand-text2 mt-1 px-1">
        <span>{data[0]?.[labelKey]?.slice(5)}</span>
        <span>{data[Math.floor(data.length / 2)]?.[labelKey]?.slice(5)}</span>
        <span>{data[data.length - 1]?.[labelKey]?.slice(5)}</span>
      </div>
    </div>
  );
};

export default function AnalyticsPanel({ token }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(30);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      console.error("analytics load failed", e);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-brand-text2" data-testid="analytics-loading">Indlæser analytics...</div>;
  if (!data) return <div className="text-brand-text2">Kunne ikke hente data</div>;

  const { summary, timeseries, top_routes, delivery_types, funnel, top_customers } = data;

  // Compare today vs yesterday
  const todayVsYday = summary.today.revenue - summary.yesterday.revenue;

  return (
    <div data-testid="analytics-panel" className="space-y-6">
      {/* Window selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Periode:</span>
        {[7, 14, 30, 90, 180, 365].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`analytics-window-${d}`}
            className={`px-3 py-1.5 text-xs font-semibold border ${days === d ? "bg-brand-dark text-white border-brand-dark" : "bg-white text-brand-dark border-brand hover:border-brand-dark"}`}
          >
            {d} dage
          </button>
        ))}
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={DollarSign}
          label="I dag"
          value={dkk(summary.today.revenue)}
          sub={`${summary.today.bookings} bookinger · ${todayVsYday >= 0 ? "+" : ""}${dkk(todayVsYday)} vs i går`}
          accent={todayVsYday > 0 ? "text-emerald-600" : todayVsYday < 0 ? "text-red-600" : ""}
        />
        <StatCard icon={Calendar} label="Denne uge" value={dkk(summary.this_week.revenue)} sub={`${summary.this_week.bookings} bookinger`} />
        <StatCard icon={Calendar} label="Denne måned" value={dkk(summary.this_month.revenue)} sub={`${summary.this_month.bookings} bookinger`} />
        <StatCard icon={TrendingUp} label="Sidste 30 dage" value={dkk(summary.last_30d.revenue)} sub={`Gns. ordreværdi: ${dkk(summary.avg_order_value_30d)}`} />
        <StatCard icon={TrendingUp} label="Sidste 90 dage" value={dkk(summary.last_90d.revenue)} sub={`${summary.last_90d.bookings} bookinger`} />
        <StatCard icon={Target} label="År-til-dato" value={dkk(summary.ytd.revenue)} sub={`${summary.ytd.bookings} bookinger`} />
        <StatCard icon={DollarSign} label="Hele tiden" value={dkk(summary.all_time.revenue)} sub={`${summary.all_time.bookings} bookinger total`} />
        <StatCard
          icon={Activity}
          label="Konvertering"
          value={`${funnel.conversion_rate_pct}%`}
          sub={`${funnel.paid}/${funnel.total} betalt`}
          accent={funnel.conversion_rate_pct >= 70 ? "text-emerald-600" : ""}
        />
      </div>

      {/* Revenue timeseries */}
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Omsætning per dag</div>
            <div className="font-display text-lg text-brand-dark mt-1">Sidste {days} dage</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-brand-text2">Total i perioden</div>
            <div className="font-display font-bold text-xl text-brand-red">
              {dkk(timeseries.reduce((a, b) => a + (b.revenue || 0), 0))}
            </div>
          </div>
        </div>
        <BarChart data={timeseries} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top routes */}
        <div className="bg-white border border-brand p-5">
          <div className="flex items-center gap-2 mb-4">
            <Truck size={18} className="text-brand-red" />
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Top ruter</div>
              <div className="font-display text-lg text-brand-dark">Mest populære leveringer</div>
            </div>
          </div>
          {top_routes.length === 0 ? (
            <div className="text-brand-text2 text-sm">Ingen ruter endnu</div>
          ) : (
            <div className="space-y-2">
              {top_routes.map((r, i) => (
                <div key={i} className="flex items-center justify-between border-b border-brand last:border-b-0 pb-2">
                  <div>
                    <div className="font-semibold text-brand-dark text-sm">{r.route}</div>
                    <div className="text-xs text-brand-text2">{r.count} leveringer</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-brand-dark">{dkk(r.revenue)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delivery type split */}
        <div className="bg-white border border-brand p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag size={18} className="text-brand-red" />
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Leveringstyper</div>
              <div className="font-display text-lg text-brand-dark">Fordeling</div>
            </div>
          </div>
          {delivery_types.length === 0 ? (
            <div className="text-brand-text2 text-sm">Ingen data endnu</div>
          ) : (
            <div className="space-y-3">
              {delivery_types.map((t, i) => {
                const total = delivery_types.reduce((a, b) => a + b.revenue, 0);
                const pct = total ? Math.round((t.revenue / total) * 100) : 0;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-semibold text-brand-dark capitalize">{t.type.replace("_", " ")}</span>
                      <span className="text-brand-text2">{dkk(t.revenue)} · {t.count} stk</span>
                    </div>
                    <div className="w-full bg-brand h-2">
                      <div className="bg-brand-red h-2" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Funnel */}
        <div className="bg-white border border-brand p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} className="text-brand-red" />
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Konvertering & status</div>
              <div className="font-display text-lg text-brand-dark">Booking funnel</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Total bookings</div><div className="font-display font-bold text-xl">{num(funnel.total)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Betalt</div><div className="font-display font-bold text-xl text-emerald-700">{num(funnel.paid)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Pending</div><div className="font-display font-bold text-xl text-amber-700">{num(funnel.pending)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Bekræftet</div><div className="font-display font-bold text-xl">{num(funnel.confirmed)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Undervejs</div><div className="font-display font-bold text-xl text-blue-700">{num(funnel.in_transit)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Leveret</div><div className="font-display font-bold text-xl text-emerald-700">{num(funnel.delivered)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Fejlet betaling</div><div className="font-display font-bold text-xl text-red-700">{num(funnel.failed)}</div></div>
            <div className="bg-brand-bg p-3"><div className="text-xs text-brand-text2">Mislykket levering</div><div className="font-display font-bold text-xl text-red-700">{num(funnel.failed_delivery)}</div></div>
          </div>
        </div>

        {/* Top customers */}
        <div className="bg-white border border-brand p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-brand-red" />
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">VIP kunder</div>
              <div className="font-display text-lg text-brand-dark">Top 10 efter omsætning</div>
            </div>
          </div>
          {top_customers.length === 0 ? (
            <div className="text-brand-text2 text-sm">Ingen kunder endnu</div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {top_customers.map((c, i) => (
                <div key={c.email} className="flex items-center justify-between border-b border-brand last:border-b-0 pb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-brand-dark text-sm truncate">{c.name}</div>
                    <div className="text-xs text-brand-text2 truncate">{c.email} · {c.bookings} bookinger</div>
                  </div>
                  <div className="text-right ml-3">
                    <div className="font-display font-bold text-brand-dark">{dkk(c.spend)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
